import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDirections from '@mapbox/mapbox-gl-directions/dist/mapbox-gl-directions';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { poisAPI, categoriesAPI, layersAPI, kmlAPI } from '../services/api';
import { POI, Category, Layer, MapFilters, POI_STATUS_LABELS } from '../types';
import * as toGeoJSON from '@tmcw/togeojson';
import { distance } from '@turf/distance';
import { area } from '@turf/area';
import { point, polygon } from '@turf/helpers';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-directions/dist/mapbox-gl-directions.css';
import './MapPage.css';

const MAPBOX_TOKEN = 'pk.eyJ1IjoiYmFndXMtZ24iLCJhIjoiY21mNnZ6aDU3MGlxdzJqb20zYnVtN3FqYSJ9.cbDl3pv8xYek_pUQiM3huQ';
const SUKOHARJO_CENTER: [number, number] = [110.8, -7.65]; // [lng, lat]

mapboxgl.accessToken = MAPBOX_TOKEN;

export const MapPage: React.FC = () => {
  const { t } = useLanguage();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const queryClient = useQueryClient();
  const [mapLoaded, setMapLoaded] = useState(false);
  const [filters, setFilters] = useState<MapFilters>({
    categories: [],
    layers: [],
    search: '',
    isFeatured: false,
  });
  const [selectedPOI, setSelectedPOI] = useState<POI | null>(null);
  const [boundariesLoaded, setBoundariesLoaded] = useState(false);
  const [uploadedKMLLoaded, setUploadedKMLLoaded] = useState<{[key: string]: boolean}>({});
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [userRating, setUserRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [isSubmittingRating, setIsSubmittingRating] = useState<boolean>(false);
  
  // Navigation and measurement state
  const [measurementMode, setMeasurementMode] = useState<boolean>(false);
  const [measurementPoints, setMeasurementPoints] = useState<[number, number][]>([]);
  const [measurementDistance, setMeasurementDistance] = useState<number | null>(null);
  const measurementPointsRef = useRef<[number, number][]>([]);
  
  // Area measurement state
  const [areaMode, setAreaMode] = useState<boolean>(false);
  const [areaPoints, setAreaPoints] = useState<[number, number][]>([]);
  const [areaSize, setAreaSize] = useState<number | null>(null);
  const areaPointsRef = useRef<[number, number][]>([]);
  const [directionsControl, setDirectionsControl] = useState<MapboxDirections | null>(null);
  const [navigationMode, setNavigationMode] = useState<boolean>(false);
  const [routeInfo, setRouteInfo] = useState<{
    distance: number;
    duration: number;
    instructions: any[];
  } | null>(null);

  // Facility search state
  const [facilitySearchQuery, setFacilitySearchQuery] = useState<string>('');
  const [facilitySearchResults, setFacilitySearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  const [searchMarkers, setSearchMarkers] = useState<mapboxgl.Marker[]>([]);

  // Fetch data
  const { data: poisData } = useQuery({
    queryKey: ['pois', filters],
    queryFn: () =>
      poisAPI.getAll({
        category: filters.categories.length ? filters.categories.join(',') : undefined,
        layer: filters.layers.length ? filters.layers.join(',') : undefined,
        search: filters.search || undefined,
        isFeatured: filters.isFeatured || undefined,
        isActive: true,
      }),
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesAPI.getAll({ isActive: true }),
  });

  const { data: layersData } = useQuery({
    queryKey: ['layers'],
    queryFn: () => layersAPI.getAll({ isActive: true }),
  });

  const { data: kmlFiles } = useQuery({
    queryKey: ['kml-files'],
    queryFn: () => kmlAPI.getFiles(),
  });

  // Rating mutation
  const ratingMutation = useMutation({
    mutationFn: ({ poiId, rating }: { poiId: string; rating: number }) => 
      poisAPI.submitRating(poiId, rating),
    onMutate: () => {
      setIsSubmittingRating(true);
    },
    onSuccess: (data) => {
      // Update the selected POI with new rating data
      if (selectedPOI && data.data) {
        setSelectedPOI(prev => prev ? {
          ...prev,
          rating: data.data?.rating || prev.rating,
          reviewCount: data.data?.reviewCount || prev.reviewCount
        } : null);
      }
      
      // Invalidate queries to refresh POI data
      queryClient.invalidateQueries({ queryKey: ['pois'] });
      
      // Show success feedback (optional)
      console.log('Rating submitted successfully!');
      setIsSubmittingRating(false);
    },
    onError: (error) => {
      console.error('Failed to submit rating:', error);
      setIsSubmittingRating(false);
      // You could add a toast notification here
    },
  });

  // Function to load KML file and convert to GeoJSON
  const loadKMLBoundaries = async () => {
    try {
      const response = await fetch('/assets/sukoharjo-boundaries.kml');
      if (!response.ok) {
        console.warn('KML file not found at /assets/sukoharjo-boundaries.kml');
        return null;
      }
      
      const kmlText = await response.text();
      const parser = new DOMParser();
      const kmlDoc = parser.parseFromString(kmlText, 'text/xml');
      
      // Convert KML to GeoJSON
      const geoJson = toGeoJSON.kml(kmlDoc);
      
      // Debug: Log the first feature's properties to help identify correct field names
      if (geoJson.features && geoJson.features.length > 0) {
        console.log('KML converted to GeoJSON. First feature properties:', geoJson.features[0].properties);
        console.log('All available properties:', Object.keys(geoJson.features[0].properties || {}));
      }
      
      return geoJson;
    } catch (error) {
      console.error('Error loading KML boundaries:', error);
      return null;
    }
  };

  // Function to load uploaded KML file
  const loadUploadedKML = async (filename: string) => {
    try {
      const response = await kmlAPI.getFileContent(filename);
      if (!response.data?.content) {
        console.error(`No content found in KML file ${filename}`);
        return null;
      }
      
      const kmlText = response.data.content;
      
      const parser = new DOMParser();
      const kmlDoc = parser.parseFromString(kmlText, 'text/xml');
      
      // Convert KML to GeoJSON
      const geoJson = toGeoJSON.kml(kmlDoc);
      
      return geoJson;
    } catch (error) {
      console.error(`Error loading KML file ${filename}:`, error);
      return null;
    }
  };

  // Function to add uploaded KML boundary layer to map
  const addUploadedKMLLayer = (geoJsonData: any, filename: string) => {
    if (!map.current || !geoJsonData) return;
    
    const sourceId = `boundaries-${filename}`;
    const fillLayerId = `boundaries-fill-${filename}`;
    const lineLayerId = `boundaries-line-${filename}`;
    const labelsLayerId = `boundaries-labels-${filename}`;

    // Add source
    map.current.addSource(sourceId, {
      type: 'geojson',
      data: geoJsonData
    });

    // Add fill layer (polygon fill)
    map.current.addLayer({
      id: fillLayerId,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': '#e11d48',
        'fill-opacity': 0.1
      }
    });

    // Add line layer (polygon outline)
    map.current.addLayer({
      id: lineLayerId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': '#e11d48',
        'line-width': 2,
        'line-opacity': 0.8
      }
    });

    // Add labels
    map.current.addLayer({
      id: labelsLayerId,
      type: 'symbol',
      source: sourceId,
      layout: {
        'text-field': [
          'case',
          ['has', 'NAME_3'], ['get', 'NAME_3'],
          ['has', 'Name'], ['get', 'Name'],
          ['has', 'name'], ['get', 'name'], 
          ['has', 'NAME'], ['get', 'NAME'],
          ['has', 'description'], ['get', 'description'],
          ['has', 'Description'], ['get', 'Description'],
          ['has', 'DESCRIPTION'], ['get', 'DESCRIPTION'],
          'Region' // fallback
        ],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-anchor': 'center'
      },
      paint: {
        'text-color': '#e11d48',
        'text-halo-color': 'white',
        'text-halo-width': 1
      }
    });

    // Add hover effect
    map.current.on('mouseenter', fillLayerId, () => {
      map.current!.getCanvas().style.cursor = 'pointer';
    });

    map.current.on('mouseleave', fillLayerId, () => {
      map.current!.getCanvas().style.cursor = '';
    });

    // Add click event for boundaries
    map.current.on('click', fillLayerId, (e) => {
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const properties = feature.properties || {};
        
        const name = properties.NAME_3 || 
                    properties.Name || 
                    properties.name || 
                    properties.NAME || 
                    properties.description ||
                    properties.Description ||
                    properties.DESCRIPTION ||
                    'Unknown Region';
        
        new mapboxgl.Popup()
          .setLngLat(e.lngLat)
          .setHTML(`<div><strong>${name}</strong></div>`)
          .addTo(map.current!);
      }
    });
  };

  // Function to remove uploaded KML layer
  const removeUploadedKMLLayer = (filename: string) => {
    if (!map.current) return;
    
    const sourceId = `boundaries-${filename}`;
    const fillLayerId = `boundaries-fill-${filename}`;
    const lineLayerId = `boundaries-line-${filename}`;
    const labelsLayerId = `boundaries-labels-${filename}`;
    
    // Remove layers
    if (map.current.getLayer(labelsLayerId)) map.current.removeLayer(labelsLayerId);
    if (map.current.getLayer(lineLayerId)) map.current.removeLayer(lineLayerId);
    if (map.current.getLayer(fillLayerId)) map.current.removeLayer(fillLayerId);
    
    // Remove source
    if (map.current.getSource(sourceId)) map.current.removeSource(sourceId);
  };

  // Function to add boundary layer to map
  const addBoundaryLayer = (geoJsonData: any) => {
    if (!map.current || !geoJsonData) return;

    // Add source
    map.current.addSource('boundaries', {
      type: 'geojson',
      data: geoJsonData
    });

    // Add fill layer (polygon fill)
    map.current.addLayer({
      id: 'boundaries-fill',
      type: 'fill',
      source: 'boundaries',
      paint: {
        'fill-color': '#088',
        'fill-opacity': 0.1
      }
    });

    // Add line layer (polygon outline)
    map.current.addLayer({
      id: 'boundaries-line',
      type: 'line',
      source: 'boundaries',
      paint: {
        'line-color': '#088',
        'line-width': 2,
        'line-opacity': 0.8
      }
    });

    // Add labels if the GeoJSON has name properties
    map.current.addLayer({
      id: 'boundaries-labels',
      type: 'symbol',
      source: 'boundaries',
      layout: {
        'text-field': [
          'case',
          ['has', 'NAME_3'], ['get', 'NAME_3'],
          ['has', 'Name'], ['get', 'Name'],
          ['has', 'name'], ['get', 'name'], 
          ['has', 'NAME'], ['get', 'NAME'],
          ['has', 'description'], ['get', 'description'],
          ['has', 'Description'], ['get', 'Description'],
          ['has', 'DESCRIPTION'], ['get', 'DESCRIPTION'],
          'District' // fallback
        ],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-anchor': 'center'
      },
      paint: {
        'text-color': '#088',
        'text-halo-color': 'white',
        'text-halo-width': 1
      }
    });

    // Add hover effect
    map.current.on('mouseenter', 'boundaries-fill', () => {
      map.current!.getCanvas().style.cursor = 'pointer';
    });

    map.current.on('mouseleave', 'boundaries-fill', () => {
      map.current!.getCanvas().style.cursor = '';
    });

    // Add click event for boundaries
    map.current.on('click', 'boundaries-fill', (e) => {
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const properties = feature.properties || {};
        
        // Use NAME_3 field from SimpleData, with fallbacks
        const name = properties.NAME_3 || 
                    properties.Name || 
                    properties.name || 
                    properties.NAME || 
                    properties.description ||
                    properties.Description ||
                    properties.DESCRIPTION ||
                    'Unknown Boundary';
        
        console.log('Boundary clicked - Properties:', properties); // Debug log
        
        // Create a simple popup for the boundary
        new mapboxgl.Popup()
          .setLngLat(e.lngLat)
          .setHTML(`<div><strong>${name}</strong></div>`)
          .addTo(map.current!);
      }
    });
  };

  // When no categories and no districts are selected, show no POIs
  const rawPois = poisData?.data || [];
  const pois = (filters.categories.length === 0 && filters.layers.length === 0) ? [] : rawPois;
  const categories = categoriesData?.data || [];
  const layers = layersData?.data || [];

  // Helper function to format opening hours
  const formatOpeningHours = (openingHours: POI['openingHours']) => {
    if (!openingHours) return null;
    
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    
    return days.map((day, index) => {
      const hours = openingHours[day];
      if (!hours) return null;
      
      return {
        day: dayNames[index],
        hours: hours.isClosed ? 'Closed' : `${hours.open} - ${hours.close}`
      };
    }).filter((item): item is { day: string; hours: string } => item !== null);
  };

  // Helper function to handle star rating clicks
  const handleRatingClick = async (rating: number) => {
    if (!selectedPOI || isSubmittingRating) return;
    
    setUserRating(rating);
    
    try {
      await ratingMutation.mutateAsync({
        poiId: selectedPOI._id,
        rating: rating
      });
    } catch (error) {
      // Error is already handled in the mutation
      console.error('Error submitting rating:', error);
    }
  };

  // Helper function to generate Google Maps URL
  const getGoogleMapsUrl = (lat: number, long: number) => {
    return `https://www.google.com/maps?q=${lat},${long}`;
  };

  // Helper function to convert YouTube URL to embed format
  const getYouTubeEmbedUrl = (url: string) => {
    const videoIdMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    if (videoIdMatch) {
      return `https://www.youtube.com/embed/${videoIdMatch[1]}`;
    }
    return url;
  };

  // Helper function to convert Vimeo URL to embed format
  const getVimeoEmbedUrl = (url: string) => {
    const videoIdMatch = url.match(/vimeo\.com\/(\d+)/);
    if (videoIdMatch) {
      return `https://player.vimeo.com/video/${videoIdMatch[1]}`;
    }
    return url;
  };

  // Facility search functions
  const searchFacilities = async (query: string) => {
    if (!query.trim()) {
      setFacilitySearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      // Search around Sukoharjo area with a bias
      const center = SUKOHARJO_CENTER;
      const proximity = `${center[0]},${center[1]}`;
      
      // Define category types for different facility searches
      const categoryTypes = {
        'hotel': 'accommodation',
        'penginapan': 'accommodation',
        'wisma': 'accommodation',
        'restaurant': 'food_and_drink',
        'restoran': 'food_and_drink',
        'warung': 'food_and_drink',
        'rumah makan': 'food_and_drink',
        'cafe': 'food_and_drink',
        'gas': 'fuel',
        'spbu': 'fuel',
        'pertamina': 'fuel',
        'shell': 'fuel',
        'hospital': 'healthcare',
        'rumah sakit': 'healthcare',
        'klinik': 'healthcare',
        'puskesmas': 'healthcare',
        'school': 'education',
        'sekolah': 'education',
        'universitas': 'education',
        'kampus': 'education',
        'bank': 'financial',
        'atm': 'financial',
        'pharmacy': 'healthcare',
        'apotek': 'healthcare',
        'market': 'shopping',
        'pasar': 'shopping',
        'mall': 'shopping',
        'toko': 'shopping',
        'minimarket': 'shopping',
        'indomaret': 'shopping',
        'alfamart': 'shopping',
        'bus': 'transit',
        'terminal': 'transit',
        'stasiun': 'transit',
        'airport': 'transit',
        'bandara': 'transit'
      };

      let results = [];
      
      // Try multiple search strategies
      const searchStrategies = [
        // Strategy 1: Exact search with category in Sukoharjo area
        {
          url: `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?` +
                `access_token=${MAPBOX_TOKEN}&` +
                `proximity=${proximity}&` +
                `bbox=110.6,-7.8,111.0,-7.5&` +
                `limit=10&country=ID`,
          withCategory: true
        },
        // Strategy 2: Broader search in Central Java area
        {
          url: `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query + ' sukoharjo')}.json?` +
                `access_token=${MAPBOX_TOKEN}&` +
                `proximity=${proximity}&` +
                `bbox=110.3,-7.9,111.3,-7.2&` + // Broader Central Java area
                `limit=10&country=ID`,
          withCategory: false
        },
        // Strategy 3: Search in nearby larger cities (Solo/Surakarta)
        {
          url: `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query + ' solo surakarta')}.json?` +
                `access_token=${MAPBOX_TOKEN}&` +
                `proximity=${proximity}&` +
                `bbox=110.5,-7.8,110.9,-7.4&` +
                `limit=10&country=ID`,
          withCategory: false
        }
      ];

      for (const strategy of searchStrategies) {
        let apiUrl = strategy.url;
        
        // Add category filter if applicable and query matches facility types
        if (strategy.withCategory) {
          const queryLower = query.toLowerCase();
          for (const [keyword, category] of Object.entries(categoryTypes)) {
            if (queryLower.includes(keyword)) {
              apiUrl += `&types=poi&category=${category}`;
              break;
            }
          }
        }

        try {
          const response = await fetch(apiUrl);
          const data = await response.json();

          if (data.features && data.features.length > 0) {
            // Filter results to prioritize closer locations
            const filteredResults = data.features.filter((feature: any) => {
              const [lng, lat] = feature.center;
              const distance = Math.sqrt(
                Math.pow(lng - center[0], 2) + Math.pow(lat - center[1], 2)
              );
              return distance <= 0.5; // Within ~50km radius
            });
            
            results = filteredResults.length > 0 ? filteredResults : data.features.slice(0, 5);
            break; // Stop searching if we found results
          }
        } catch (error) {
          console.warn(`Search strategy failed:`, error);
          continue; // Try next strategy
        }
      }

      // Always show the dropdown (even for no results) if user has typed something
      setFacilitySearchResults(results);
      setShowSearchResults(true); // Show dropdown to display "no results" message if needed
      
      if (results.length === 0) {
        console.log(`No results found for "${query}" in Sukoharjo area`);
      }
      
    } catch (error) {
      console.error('Error searching facilities:', error);
      setFacilitySearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Clear search results and markers
  const clearFacilitySearch = () => {
    setFacilitySearchQuery('');
    setFacilitySearchResults([]);
    setShowSearchResults(false);
    clearSearchMarkers();
  };

  // Clear search markers from map
  const clearSearchMarkers = () => {
    searchMarkers.forEach(marker => marker.remove());
    setSearchMarkers([]);
  };

  // Add search result markers to map
  const addSearchMarkers = (results: any[]) => {
    clearSearchMarkers();
    
    const newMarkers: mapboxgl.Marker[] = [];
    results.forEach((result, index) => {
      const [lng, lat] = result.center;
      
      // Create marker element
      const markerEl = document.createElement('div');
      markerEl.className = 'search-result-marker';
      markerEl.style.cssText = `
        background-color: #10b981;
        color: white;
        border-radius: 50%;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 12px;
        border: 2px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        cursor: pointer;
      `;
      markerEl.innerHTML = (index + 1).toString();
      
      // Create marker
      const marker = new mapboxgl.Marker(markerEl)
        .setLngLat([lng, lat])
        .addTo(map.current!);
      
      // Add click handler for marker
      markerEl.addEventListener('click', () => {
        // Pan to marker and show popup info
        if (map.current) {
          map.current.flyTo({
            center: [lng, lat],
            zoom: 16,
            duration: 1000
          });
        }
      });
      
      newMarkers.push(marker);
    });
    
    setSearchMarkers(newMarkers);
  };

  // Navigate to search result
  const navigateToSearchResult = (result: any) => {
    const [lng, lat] = result.center;
    if (directionsControl) {
      (directionsControl as MapboxDirections).setDestination([lng, lat]);
    } else {
      // Start navigation mode and set destination
      startNavigation();
      setTimeout(() => {
        if (directionsControl) {
          (directionsControl as MapboxDirections).setDestination([lng, lat]);
        }
      }, 500);
    }
    setShowSearchResults(false);
  };

  // Handle search input with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (facilitySearchQuery) {
        searchFacilities(facilitySearchQuery);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [facilitySearchQuery]);

  // Update search markers when results change
  useEffect(() => {
    if (facilitySearchResults.length > 0) {
      addSearchMarkers(facilitySearchResults);
    } else {
      clearSearchMarkers();
    }
  }, [facilitySearchResults]);

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const searchBox = document.querySelector('.facility-search-container');
      if (searchBox && !searchBox.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSearchResults]);

  // Distance measurement functions
  const startMeasurement = () => {
    console.log('Starting measurement mode');
    setMeasurementMode(true);
    setNavigationMode(false);
    setAreaMode(false); // Stop area measurement
    setMeasurementPoints([]);
    setMeasurementDistance(null);
    measurementPointsRef.current = []; // Reset ref
    if (directionsControl) {
      map.current?.removeControl(directionsControl);
    }
    // Clean up area measurement
    removeAreaVisualization();
    
    // Change cursor to crosshair
    if (map.current) {
      map.current.getCanvas().style.cursor = 'crosshair';
    }
  };

  const stopMeasurement = () => {
    console.log('Stopping measurement mode');
    
    // Reset state first
    setMeasurementMode(false);
    setMeasurementPoints([]);
    setMeasurementDistance(null);
    measurementPointsRef.current = [];
    
    // Reset cursor
    if (map.current) {
      map.current.getCanvas().style.cursor = '';
    }
    
    // Delay cleanup to avoid React conflicts
    setTimeout(() => {
      try {
        // Clean up DOM elements
        const markers = document.querySelectorAll('.measurement-marker');
        markers.forEach(marker => {
          if (marker.parentElement) {
            marker.parentElement.remove();
          }
        });
        
        // Clean up map layers
        if (map.current) {
          if (map.current.getLayer && map.current.getLayer('measurement-line')) {
            map.current.removeLayer('measurement-line');
          }
          if (map.current.getSource && map.current.getSource('measurement-line')) {
            map.current.removeSource('measurement-line');
          }
        }
        console.log('Cleanup completed successfully');
      } catch (e) {
        console.log('Cleanup finished with minor issues');
      }
    }, 10);
  };

  // Simple measurement click handler using ref for current state
  const handleMeasurementClick = (e: mapboxgl.MapMouseEvent) => {
    if (!measurementMode || !map.current) return;

    const coordinates: [number, number] = [e.lngLat.lng, e.lngLat.lat];
    const currentPoints = measurementPointsRef.current;
    console.log('Measurement click at:', coordinates, 'Current points length:', currentPoints.length);
    
    if (currentPoints.length === 0) {
      // First point
      console.log('Adding first point');
      const newPoints = [coordinates];
      measurementPointsRef.current = newPoints;
      setMeasurementPoints(newPoints);
      addMeasurementMarker(coordinates, 'A');
    } else if (currentPoints.length === 1) {
      // Second point - calculate distance
      console.log('Adding second point');
      const from = point(currentPoints[0]);
      const to = point(coordinates);
      const distanceInKm = distance(from, to, { units: 'kilometers' });
      console.log('Distance calculated:', distanceInKm, 'km');
      
      const newPoints = [currentPoints[0], coordinates];
      measurementPointsRef.current = newPoints;
      setMeasurementPoints(newPoints);
      setMeasurementDistance(distanceInKm);
      addMeasurementMarker(coordinates, 'B');
      addMeasurementLine(currentPoints[0], coordinates);
    } else {
      // Reset for new measurement
      console.log('Resetting measurement');
      // Clear existing
      const markers = document.querySelectorAll('.measurement-marker');
      markers.forEach(marker => marker.parentElement?.remove());
      try {
        if (map.current.getLayer('measurement-line')) {
          map.current.removeLayer('measurement-line');
        }
        if (map.current.getSource('measurement-line')) {
          map.current.removeSource('measurement-line');
        }
      } catch (e) {
        // Ignore cleanup errors
      }
      
      // Start new measurement
      const newPoints = [coordinates];
      measurementPointsRef.current = newPoints;
      setMeasurementPoints(newPoints);
      setMeasurementDistance(null);
      addMeasurementMarker(coordinates, 'A');
    }
  };

  const addMeasurementMarker = (coordinates: [number, number], label: string) => {
    if (!map.current) return;

    const el = document.createElement('div');
    el.className = 'measurement-marker';
    el.innerHTML = label;
    el.style.cssText = `
      background-color: #ff6b6b;
      color: white;
      border-radius: 50%;
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 14px;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    `;

    new mapboxgl.Marker(el)
      .setLngLat(coordinates)
      .addTo(map.current);
  };

  const addMeasurementLine = (start: [number, number], end: [number, number]) => {
    if (!map.current) return;

    const geojson = {
      type: 'Feature' as const,
      geometry: {
        type: 'LineString' as const,
        coordinates: [start, end]
      },
      properties: {}
    };

    map.current.addSource('measurement-line', {
      type: 'geojson',
      data: geojson
    });

    map.current.addLayer({
      id: 'measurement-line',
      type: 'line',
      source: 'measurement-line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#ff6b6b',
        'line-width': 3,
        'line-dasharray': [2, 2]
      }
    });
  };

  const removeMeasurementVisualization = () => {
    console.log('Removing measurement visualization');
    if (!map.current) {
      console.log('No map available for cleanup');
      return;
    }
    
    try {
      // Remove measurement markers with more defensive checks
      const markers = document.querySelectorAll('.measurement-marker');
      console.log('Found', markers.length, 'measurement markers to remove');
      markers.forEach((marker, index) => {
        try {
          const parent = marker.parentElement;
          if (parent && parent.parentElement) {
            parent.parentElement.removeChild(parent);
            console.log(`Removed marker ${index + 1}`);
          }
        } catch (markerError) {
          console.error(`Error removing marker ${index + 1}:`, markerError);
        }
      });

      // Remove measurement line with defensive checks
      try {
        if (map.current && map.current.getLayer && map.current.getLayer('measurement-line')) {
          console.log('Removing measurement line layer');
          map.current.removeLayer('measurement-line');
        }
      } catch (layerError) {
        console.error('Error removing measurement layer:', layerError);
      }
      
      try {
        if (map.current && map.current.getSource && map.current.getSource('measurement-line')) {
          console.log('Removing measurement line source');
          map.current.removeSource('measurement-line');
        }
      } catch (sourceError) {
        console.error('Error removing measurement source:', sourceError);
      }
      
      console.log('Measurement visualization cleanup completed');
    } catch (error) {
      console.error('Error removing measurement visualization:', error);
    }
  };

  // Area measurement functions
  const startAreaMeasurement = () => {
    console.log('Starting area measurement');
    setAreaMode(true);
    setMeasurementMode(false);
    setNavigationMode(false);
    
    // Clear any existing area points
    setAreaPoints([]);
    areaPointsRef.current = [];
    setAreaSize(null);
    
    // Stop other modes
    stopMeasurement();
    stopNavigation();
    removeAreaVisualization();
    
    if (map.current) {
      map.current.getCanvas().style.cursor = 'crosshair';
    }
  };

  const stopAreaMeasurement = () => {
    console.log('Stopping area measurement');
    setAreaMode(false);
    setAreaPoints([]);
    areaPointsRef.current = [];
    setAreaSize(null);
    
    if (map.current) {
      map.current.getCanvas().style.cursor = '';
    }
    
    removeAreaVisualization();
  };

  const addAreaPoint = (lngLat: mapboxgl.LngLat) => {
    const newPoint: [number, number] = [lngLat.lng, lngLat.lat];
    const updatedPoints = [...areaPointsRef.current, newPoint];
    
    areaPointsRef.current = updatedPoints;
    setAreaPoints(updatedPoints);
    
    console.log('Added area point:', newPoint, 'Total points:', updatedPoints.length);
    
    // Add marker for this point
    addAreaMarker(newPoint, updatedPoints.length);
    
    // If we have 3 or more points, we can calculate area
    if (updatedPoints.length >= 3) {
      updateAreaVisualization(updatedPoints);
      calculateArea(updatedPoints);
    } else if (updatedPoints.length >= 2) {
      // Draw lines between points
      updateAreaLines(updatedPoints);
    }
  };

  const addAreaMarker = (coordinates: [number, number], index: number) => {
    if (!map.current) return;
    
    const markerEl = document.createElement('div');
    markerEl.className = 'area-marker';
    markerEl.style.width = '12px';
    markerEl.style.height = '12px';
    markerEl.style.borderRadius = '50%';
    markerEl.style.backgroundColor = '#22c55e';
    markerEl.style.border = '2px solid white';
    markerEl.style.cursor = 'pointer';
    markerEl.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.1)';
    
    // Add click handler to complete polygon (only on first point if we have 3+ points)
    if (index === 1 && areaPointsRef.current.length >= 3) {
      markerEl.style.backgroundColor = '#ef4444';
      markerEl.style.cursor = 'pointer';
      markerEl.title = 'Click to complete polygon';
      markerEl.onclick = () => {
        completeAreaMeasurement();
      };
    }
    
    new mapboxgl.Marker({ element: markerEl })
      .setLngLat(coordinates)
      .addTo(map.current);
  };

  const updateAreaLines = (points: [number, number][]) => {
    if (!map.current || points.length < 2) return;
    
    const geojson = {
      type: 'Feature' as const,
      geometry: {
        type: 'LineString' as const,
        coordinates: points
      },
      properties: {}
    };
    
    // Remove existing line source and layer
    try {
      if (map.current.getLayer('area-line')) {
        map.current.removeLayer('area-line');
      }
      if (map.current.getSource('area-line')) {
        map.current.removeSource('area-line');
      }
    } catch (e) {
      // Ignore errors
    }
    
    map.current.addSource('area-line', {
      type: 'geojson',
      data: geojson
    });
    
    map.current.addLayer({
      id: 'area-line',
      type: 'line',
      source: 'area-line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#22c55e',
        'line-width': 2,
        'line-dasharray': [2, 2]
      }
    });
  };

  const updateAreaVisualization = (points: [number, number][]) => {
    if (!map.current || points.length < 3) return;
    
    // Close the polygon by connecting back to the first point
    const closedPoints = [...points, points[0]];
    
    const polygonGeoJson = {
      type: 'Feature' as const,
      geometry: {
        type: 'Polygon' as const,
        coordinates: [closedPoints]
      },
      properties: {}
    };
    
    // Remove existing area sources and layers
    try {
      if (map.current.getLayer('area-fill')) {
        map.current.removeLayer('area-fill');
      }
      if (map.current.getLayer('area-outline')) {
        map.current.removeLayer('area-outline');
      }
      if (map.current.getSource('area-polygon')) {
        map.current.removeSource('area-polygon');
      }
      if (map.current.getLayer('area-line')) {
        map.current.removeLayer('area-line');
      }
      if (map.current.getSource('area-line')) {
        map.current.removeSource('area-line');
      }
    } catch (e) {
      // Ignore errors
    }
    
    map.current.addSource('area-polygon', {
      type: 'geojson',
      data: polygonGeoJson
    });
    
    // Add fill layer
    map.current.addLayer({
      id: 'area-fill',
      type: 'fill',
      source: 'area-polygon',
      paint: {
        'fill-color': '#22c55e',
        'fill-opacity': 0.2
      }
    });
    
    // Add outline layer
    map.current.addLayer({
      id: 'area-outline',
      type: 'line',
      source: 'area-polygon',
      paint: {
        'line-color': '#22c55e',
        'line-width': 2
      }
    });
  };

  const calculateArea = (points: [number, number][]) => {
    if (points.length < 3) {
      setAreaSize(null);
      return;
    }
    
    try {
      // Create a closed polygon for area calculation
      const closedPoints = [...points, points[0]];
      const polygonFeature = polygon([closedPoints]);
      const areaInSquareMeters = area(polygonFeature);
      const areaInHectares = areaInSquareMeters / 10000; // Convert to hectares
      
      console.log(`Calculated area: ${areaInSquareMeters.toFixed(2)} m² (${areaInHectares.toFixed(4)} ha)`);
      setAreaSize(areaInHectares);
    } catch (error) {
      console.error('Error calculating area:', error);
      setAreaSize(null);
    }
  };

  const completeAreaMeasurement = () => {
    if (areaPointsRef.current.length < 3) return;
    
    console.log('Completing area measurement');
    updateAreaVisualization(areaPointsRef.current);
    calculateArea(areaPointsRef.current);
    
    // Change cursor back to normal
    if (map.current) {
      map.current.getCanvas().style.cursor = '';
    }
    
    // Don't stop area mode - let user manually stop it to see the result
  };

  const removeAreaVisualization = () => {
    console.log('Removing area visualization');
    if (!map.current) return;
    
    try {
      // Remove area markers
      const markers = document.querySelectorAll('.area-marker');
      console.log('Found', markers.length, 'area markers to remove');
      markers.forEach((marker, index) => {
        try {
          const parent = marker.parentElement;
          if (parent && parent.parentElement) {
            parent.parentElement.removeChild(parent);
            console.log(`Removed area marker ${index + 1}`);
          }
        } catch (markerError) {
          console.error(`Error removing area marker ${index + 1}:`, markerError);
        }
      });
      
      // Remove area layers and sources
      const layersToRemove = ['area-fill', 'area-outline', 'area-line'];
      const sourcesToRemove = ['area-polygon', 'area-line'];
      
      layersToRemove.forEach(layerId => {
        try {
          if (map.current && map.current.getLayer && map.current.getLayer(layerId)) {
            map.current.removeLayer(layerId);
            console.log(`Removed layer: ${layerId}`);
          }
        } catch (layerError) {
          console.error(`Error removing layer ${layerId}:`, layerError);
        }
      });
      
      sourcesToRemove.forEach(sourceId => {
        try {
          if (map.current && map.current.getSource && map.current.getSource(sourceId)) {
            map.current.removeSource(sourceId);
            console.log(`Removed source: ${sourceId}`);
          }
        } catch (sourceError) {
          console.error(`Error removing source ${sourceId}:`, sourceError);
        }
      });
      
      console.log('Area visualization cleanup completed');
    } catch (error) {
      console.error('Error removing area visualization:', error);
    }
  };

  // Navigation functions
  const startNavigation = () => {
    if (!map.current) return;
    
    setNavigationMode(true);
    setMeasurementMode(false);
    setAreaMode(false); // Stop area measurement
    stopMeasurement();
    stopAreaMeasurement();

    // Create and add directions control
    const directions = new MapboxDirections({
      accessToken: MAPBOX_TOKEN,
      unit: 'metric',
      profile: 'mapbox/driving',
      alternatives: true,
      geometries: 'geojson',
      controls: {
        instructions: true,
        inputs: true,
        profileSwitcher: true
      },
      flyTo: true,
      placeholderOrigin: 'Choose starting point',
      placeholderDestination: 'Choose destination',
      // Custom styles
      styles: [
        {
          id: 'directions-route-line-alt',
          type: 'line',
          source: 'directions',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#bbb',
            'line-width': 4
          },
          filter: [
            'all',
            ['==', '$type', 'LineString'],
            ['!=', ['get', 'route'], 'selected']
          ]
        },
        {
          id: 'directions-route-line',
          type: 'line',
          source: 'directions',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#3887be',
            'line-width': 5,
            'line-opacity': 0.75
          },
          filter: [
            'all',
            ['==', '$type', 'LineString'],
            ['==', ['get', 'route'], 'selected']
          ]
        }
      ]
    });

    setDirectionsControl(directions);
    map.current.addControl(directions, 'top-left');

    // Listen for route calculation
    directions.on('route', (e: any) => {
      if (e.route && e.route.length > 0) {
        const route = e.route[0];
        setRouteInfo({
          distance: route.distance / 1000, // Convert to km
          duration: route.duration / 60,   // Convert to minutes
          instructions: route.legs[0]?.steps || []
        });
      }
    });

    // Listen for route clear
    directions.on('clear', () => {
      setRouteInfo(null);
    });
  };

  const stopNavigation = () => {
    setNavigationMode(false);
    setRouteInfo(null);
    if (directionsControl && map.current) {
      map.current.removeControl(directionsControl);
      setDirectionsControl(null);
    }
  };

  const navigateToPoint = (coordinates: [number, number], name: string = 'Selected Point') => {
    if (!directionsControl) {
      startNavigation();
      // Wait a bit for the directions control to be ready
      setTimeout(() => {
        if (directionsControl) {
          (directionsControl as MapboxDirections).setDestination(coordinates);
        }
      }, 500);
    } else {
      (directionsControl as MapboxDirections).setDestination(coordinates);
    }
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11', // Standard streets style
      center: SUKOHARJO_CENTER,
      zoom: 11,
      minZoom: 8,
      maxZoom: 18
    });

    map.current.on('load', async () => {
      setMapLoaded(true);
      
      // Load KML boundaries
      const boundaryData = await loadKMLBoundaries();
      if (boundaryData) {
        addBoundaryLayer(boundaryData);
        setBoundariesLoaded(true);
      }
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Handle map click events for measurement, area measurement and POI selection
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
      console.log('Map clicked', { measurementMode, areaMode, coordinates: [e.lngLat.lng, e.lngLat.lat] });
      
      if (measurementMode) {
        handleMeasurementClick(e);
      } else if (areaMode) {
        addAreaPoint(e.lngLat);
      } else {
        setSelectedPOI(null); // Clear POI selection on map click
      }
    };

    map.current.on('click', handleMapClick);

    // Clean up event listener
    return () => {
      if (map.current) {
        map.current.off('click', handleMapClick);
      }
    };
  }, [mapLoaded, measurementMode, areaMode]);

  /* TEMPORARILY DISABLED FOR DEBUGGING - Handle measurement clicks separately
  useEffect(() => {
    if (!map.current || !mapLoaded || !measurementMode) return;

    const handleMeasurementClickDirect = (e: mapboxgl.MapMouseEvent) => {
      console.log('Measurement click detected', { coordinates: [e.lngLat.lng, e.lngLat.lat] });
      const coordinates: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      
      // Get current measurement points directly from state
      setMeasurementPoints(currentPoints => {
        console.log('Current points length:', currentPoints.length);
        
        if (currentPoints.length === 0) {
          // First point
          console.log('Adding first measurement point');
          addMeasurementMarker(coordinates, 'A');
          return [coordinates];
        } else if (currentPoints.length === 1) {
          // Second point - calculate distance
          console.log('Adding second measurement point and calculating distance');
          
          // Calculate distance using Turf.js
          const from = point(currentPoints[0]);
          const to = point(coordinates);
          const distanceInKm = distance(from, to, { units: 'kilometers' });
          console.log('Calculated distance:', distanceInKm, 'km');
          setMeasurementDistance(distanceInKm);
          
          addMeasurementMarker(coordinates, 'B');
          addMeasurementLine(currentPoints[0], coordinates);
          return [...currentPoints, coordinates];
        } else {
          // Reset for new measurement
          console.log('Resetting measurement for new measurement');
          setTimeout(() => removeMeasurementVisualization(), 50);
          setMeasurementDistance(null);
          setTimeout(() => addMeasurementMarker(coordinates, 'A'), 100);
          return [coordinates];
        }
      });
    };

    map.current.on('click', handleMeasurementClickDirect);

    return () => {
      if (map.current) {
        map.current.off('click', handleMeasurementClickDirect);
      }
    };
  }, [measurementMode, mapLoaded]);
  */

  // Update markers when data changes
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    
    // If no POIs after filtering, or if no filters are selected and we want to hide all, clear markers
    if (!pois.length) {
      // Clear existing markers
      const existingMarkers = document.querySelectorAll('.mapbox-marker');
      existingMarkers.forEach((marker) => marker.remove());
      return;
    }

    // Clear existing markers and close any open popup
    const existingMarkers = document.querySelectorAll('.mapbox-marker');
    existingMarkers.forEach((marker) => marker.remove());
    
    // Close React modal if open
    setSelectedPOI(null);

    // Add new markers
    pois.forEach((poi) => {
      const category = typeof poi.category === 'object' ? poi.category : null;
      const layer = typeof poi.layer === 'object' ? poi.layer : null;

      // Create marker element
      const markerEl = document.createElement('div');
      markerEl.className = 'mapbox-marker';
      markerEl.style.backgroundColor = category?.color || '#3b82f6';
      markerEl.style.width = '12px';
      markerEl.style.height = '12px';
      markerEl.style.borderRadius = '50%';
      markerEl.style.border = '2px solid white';
      markerEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
      markerEl.style.cursor = 'pointer';

      if (poi.isFeatured) {
        markerEl.style.width = '16px';
        markerEl.style.height = '16px';
        markerEl.style.border = '3px solid #fbbf24';
      }

      // No popup needed - using React modal only

      // Create marker
      const marker = new mapboxgl.Marker(markerEl)
        .setLngLat([poi.long, poi.lat]) // [longitude, latitude] for Mapbox
        .addTo(map.current!);

      // Add click handler - only use React modal
      markerEl.addEventListener('click', (e) => {
        e.stopPropagation();
        
        // Reset modal states and set selected POI for React modal
        setSelectedImageIndex(null);
        setUserRating(0);
        setHoveredRating(0);
        setIsSubmittingRating(false);
        setSelectedPOI(poi);
      });
    });
  }, [pois, mapLoaded]);

  // Note: District boundaries removed as requested

  const handleCategoryToggle = (categoryId: string) => {
    setFilters((prev) => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter((id) => id !== categoryId)
        : [...prev.categories, categoryId],
    }));
  };

  const handleLayerToggle = (layerId: string) => {
    setFilters((prev) => ({
      ...prev,
      layers: prev.layers.includes(layerId)
        ? prev.layers.filter((id) => id !== layerId)
        : [...prev.layers, layerId],
    }));
  };

  // Select all categories
  const handleSelectAllCategories = () => {
    const allCategoryIds = categories.map(category => category._id);
    setFilters(prev => ({ ...prev, categories: allCategoryIds }));
  };

  // Deselect all categories
  const handleDeselectAllCategories = () => {
    setFilters(prev => ({ ...prev, categories: [] }));
  };

  // Select all districts/layers
  const handleSelectAllLayers = () => {
    const allLayerIds = layers.map(layer => layer._id);
    setFilters(prev => ({ ...prev, layers: allLayerIds }));
  };

  // Deselect all districts/layers
  const handleDeselectAllLayers = () => {
    setFilters(prev => ({ ...prev, layers: [] }));
  };

  // Handle uploaded KML file toggle
  const handleUploadedKMLToggle = async (filename: string, enabled: boolean) => {
    if (enabled) {
      // Load and add the KML layer
      const geoJsonData = await loadUploadedKML(filename);
      if (geoJsonData) {
        addUploadedKMLLayer(geoJsonData, filename);
        setUploadedKMLLoaded(prev => ({ ...prev, [filename]: true }));
      }
    } else {
      // Remove the KML layer
      removeUploadedKMLLayer(filename);
      setUploadedKMLLoaded(prev => ({ ...prev, [filename]: false }));
    }
  };

  return (
    <div className="map-page">
      {/* Sidebar */}
      <div className="map-sidebar">
        <div className="sidebar-header">
          <h1>{t('app.title')}</h1>
          <p>{t('app.subtitle')}</p>
          
          {/* Language Switcher */}
          <div style={{ marginTop: '10px' }}>
            <LanguageSwitcher />
          </div>
        </div>

        {/* Search */}
        <div className="search-section">
          <input
            type="text"
            placeholder={t('map.searchLocations')}
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            className="search-input"
          />
        </div>

        {/* Categories Filter */}
        <div className="filter-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <h3 style={{ margin: 0 }}>{t('map.categories')}</h3>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                onClick={handleSelectAllCategories}
                disabled={filters.categories.length === categories.length}
                style={{
                  background: filters.categories.length === categories.length ? '#e5e7eb' : '#3b82f6',
                  color: filters.categories.length === categories.length ? '#9ca3af' : 'white',
                  border: 'none',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  cursor: filters.categories.length === categories.length ? 'default' : 'pointer',
                  fontSize: '10px',
                  fontWeight: '500'
                }}
              >
                {t('common.selectAll')}
              </button>
              <button
                onClick={handleDeselectAllCategories}
                disabled={filters.categories.length === 0}
                style={{
                  background: filters.categories.length === 0 ? '#e5e7eb' : '#ef4444',
                  color: filters.categories.length === 0 ? '#9ca3af' : 'white',
                  border: 'none',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  cursor: filters.categories.length === 0 ? 'default' : 'pointer',
                  fontSize: '10px',
                  fontWeight: '500'
                }}
              >
                {t('common.clearAll')}
              </button>
            </div>
          </div>
          <div className="filter-list">
            {categories.map((category) => (
              <label key={category._id} className="filter-item">
                <input
                  type="checkbox"
                  checked={filters.categories.includes(category._id)}
                  onChange={() => handleCategoryToggle(category._id)}
                />
                <span
                  className="filter-color"
                  style={{ backgroundColor: category.color }}
                />
                <span>{category.name}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Layers Filter */}
        <div className="filter-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <h3 style={{ margin: 0 }}>{t('map.districts')}</h3>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                onClick={handleSelectAllLayers}
                disabled={filters.layers.length === layers.length}
                style={{
                  background: filters.layers.length === layers.length ? '#e5e7eb' : '#3b82f6',
                  color: filters.layers.length === layers.length ? '#9ca3af' : 'white',
                  border: 'none',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  cursor: filters.layers.length === layers.length ? 'default' : 'pointer',
                  fontSize: '10px',
                  fontWeight: '500'
                }}
              >
                {t('common.selectAll')}
              </button>
              <button
                onClick={handleDeselectAllLayers}
                disabled={filters.layers.length === 0}
                style={{
                  background: filters.layers.length === 0 ? '#e5e7eb' : '#ef4444',
                  color: filters.layers.length === 0 ? '#9ca3af' : 'white',
                  border: 'none',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  cursor: filters.layers.length === 0 ? 'default' : 'pointer',
                  fontSize: '10px',
                  fontWeight: '500'
                }}
              >
                {t('common.clearAll')}
              </button>
            </div>
          </div>
          <div className="filter-list">
            {layers.map((layer) => (
              <label key={layer._id} className="filter-item">
                <input
                  type="checkbox"
                  checked={filters.layers.includes(layer._id)}
                  onChange={() => handleLayerToggle(layer._id)}
                />
                <span
                  className="filter-color"
                  style={{ backgroundColor: layer.color }}
                />
                <span>{layer.name}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Featured Toggle */}
        <div className="filter-section">
          <label className="filter-item">
            <input
              type="checkbox"
              checked={filters.isFeatured}
              onChange={(e) => setFilters((prev) => ({ ...prev, isFeatured: e.target.checked }))}
            />
            <span>{t('map.featuredOnly')}</span>
          </label>
        </div>

        {/* Boundary Toggle */}
        {boundariesLoaded && (
          <div className="filter-section">
            <label className="filter-item">
              <input
                type="checkbox"
                defaultChecked={true}
                onChange={(e) => {
                  if (!map.current) return;
                  const visibility = e.target.checked ? 'visible' : 'none';
                  map.current.setLayoutProperty('boundaries-fill', 'visibility', visibility);
                  map.current.setLayoutProperty('boundaries-line', 'visibility', visibility);
                  map.current.setLayoutProperty('boundaries-labels', 'visibility', visibility);
                }}
              />
              <span>Show boundaries</span>
            </label>
          </div>
        )}

        {/* Uploaded KML Files */}
        {kmlFiles?.data && kmlFiles.data.length > 0 && (
          <div className="filter-section">
            <h3 style={{ margin: '0 0 8px' }}>Additional Boundaries</h3>
            <div className="filter-list">
              {kmlFiles.data.map((file) => (
                <label key={file.filename} className="filter-item">
                  <input
                    type="checkbox"
                    checked={uploadedKMLLoaded[file.filename] || false}
                    onChange={(e) => handleUploadedKMLToggle(file.filename, e.target.checked)}
                  />
                  <span
                    className="filter-color"
                    style={{ backgroundColor: '#e11d48' }}
                  />
                  <span title={file.originalName}>
                    {file.originalName.length > 20 
                      ? file.originalName.substring(0, 20) + '...' 
                      : file.originalName
                    }
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Navigation Tools */}
        <div className="filter-section">
          <h3 style={{ margin: '0 0 8px' }}>{t('map.navigationTools')}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button
              onClick={measurementMode ? stopMeasurement : startMeasurement}
              style={{
                background: measurementMode ? '#ef4444' : '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '10px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {measurementMode ? `📏 ${t('map.stopMeasuring')}` : `📏 ${t('map.measureDistance')}`}
            </button>
            
            <button
              onClick={navigationMode ? stopNavigation : startNavigation}
              style={{
                background: navigationMode ? '#ef4444' : '#10b981',
                color: 'white',
                border: 'none',
                padding: '10px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {navigationMode ? `🧭 ${t('map.stopNavigation')}` : `🧭 ${t('map.getDirections')}`}
            </button>

            <button
              onClick={areaMode ? stopAreaMeasurement : startAreaMeasurement}
              style={{
                background: areaMode ? '#ef4444' : '#22c55e',
                color: 'white',
                border: 'none',
                padding: '10px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {areaMode ? `📐 ${t('map.stopMeasuring')}` : `📐 ${t('map.measureArea')}`}
            </button>
            
            {measurementMode && (
              <div style={{ 
                background: '#f3f4f6', 
                padding: '10px', 
                borderRadius: '6px', 
                fontSize: '12px',
                color: '#374151'
              }}>
                {measurementPoints.length === 0 && 'Click on map to start measuring'}
                {measurementPoints.length === 1 && 'Click second point to measure distance'}
                {measurementDistance !== null && (
                  <div style={{ fontWeight: 'bold', color: '#059669' }}>
                    Distance: {measurementDistance.toFixed(2)} km
                  </div>
                )}
              </div>
            )}

            {areaMode && (
              <div style={{
                background: '#f0fdf4',
                padding: '10px',
                borderRadius: '6px',
                fontSize: '12px',
                color: '#064e3b',
                border: '1px solid #bbf7d0'
              }}>
                {areaPoints.length < 3 && 'Click on map to add points. Add at least 3 points then click the first point to complete the polygon.'}
                {areaPoints.length >= 3 && (
                  <div>
                    <div>Points: {areaPoints.length}</div>
                    {areaSize !== null && (
                      <div style={{ fontWeight: 'bold', color: '#065f46' }}>
                        Area: {areaSize.toFixed(4)} ha
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="stats-section">
          <p>{t('map.showingLocations', { count: pois.length })}</p>
        </div>

        {/* Admin Link */}
        <div className="admin-link">
          <a href="/login">Admin Login</a>
        </div>
      </div>

      {/* Map */}
      <div className="map-container">
        <div ref={mapContainer} className="map" />
        
        {/* Facility Search Box */}
        <div className="facility-search-container" style={{
          position: 'absolute',
          top: '20px',
          right: routeInfo ? '340px' : '20px', // Adjust position based on route panel
          zIndex: 1000,
          transition: 'right 0.3s ease'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            border: '1px solid #e5e7eb',
            overflow: 'visible'
          }}>
            {/* Search Input */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              minWidth: '300px'
            }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text"
                  placeholder="Search: hotel, restaurant, spbu, rumah sakit, bank, pasar..."
                  value={facilitySearchQuery}
                  onChange={(e) => setFacilitySearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 40px 10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  onFocus={() => facilitySearchResults.length > 0 && setShowSearchResults(true)}
                />
                {/* Search Icon */}
                <div style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: isSearching ? '#10b981' : '#6b7280',
                  pointerEvents: 'none'
                }}>
                  {isSearching ? (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #10b981',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                  ) : (
                    <span style={{ fontSize: '16px' }}>🔍</span>
                  )}
                </div>
                {/* Clear Button */}
                {facilitySearchQuery && (
                  <button
                    onClick={clearFacilitySearch}
                    style={{
                      position: 'absolute',
                      right: '35px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: '#6b7280',
                      cursor: 'pointer',
                      fontSize: '18px',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            
            {/* Search Results Dropdown */}
            {showSearchResults && (
              facilitySearchResults.length > 0 ? (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #e5e7eb',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                maxHeight: '300px',
                overflowY: 'auto',
                zIndex: 1001
              }}>
                {facilitySearchResults.map((result, index) => {
                  const [lng, lat] = result.center;
                  const distance = map.current ? 
                    Math.round(
                      Math.sqrt(
                        Math.pow(lng - SUKOHARJO_CENTER[0], 2) + 
                        Math.pow(lat - SUKOHARJO_CENTER[1], 2)
                      ) * 111 * 1000 // Rough conversion to meters
                    ) : null;
                  
                  return (
                    <div
                      key={result.id || index}
                      style={{
                        padding: '12px',
                        borderBottom: index < facilitySearchResults.length - 1 ? '1px solid #f3f4f6' : 'none',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                      onClick={() => {
                        // Pan to location
                        if (map.current) {
                          map.current.flyTo({
                            center: [lng, lat],
                            zoom: 16,
                            duration: 1000
                          });
                        }
                        setShowSearchResults(false);
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px'
                      }}>
                        {/* Result Number */}
                        <div style={{
                          backgroundColor: '#10b981',
                          color: 'white',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          flexShrink: 0
                        }}>
                          {index + 1}
                        </div>
                        
                        {/* Result Info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: '600',
                            fontSize: '14px',
                            color: '#374151',
                            marginBottom: '2px'
                          }}>
                            {result.text || result.place_name}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            lineHeight: '1.4'
                          }}>
                            {result.place_name}
                          </div>
                          {distance && (
                            <div style={{
                              fontSize: '11px',
                              color: '#10b981',
                              marginTop: '4px'
                            }}>
                              ~{distance > 1000 ? `${(distance/1000).toFixed(1)} km` : `${distance}m`} away
                            </div>
                          )}
                        </div>
                        
                        {/* Navigate Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigateToSearchResult(result);
                          }}
                          style={{
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 8px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                          title="Get directions"
                        >
                          🧭
                        </button>
                      </div>
                    </div>
                  );
                })}
                
                {/* Clear Results Button */}
                <div style={{
                  padding: '8px 12px',
                  borderTop: '1px solid #f3f4f6',
                  background: '#f9fafb'
                }}>
                  <button
                    onClick={clearFacilitySearch}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#6b7280',
                      fontSize: '12px',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'center'
                    }}
                  >
                    Clear all results
                  </button>
                </div>
              </div>
              ) : (
                // No Results Message
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                  padding: '20px',
                  textAlign: 'center',
                  zIndex: 1001
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontSize: '24px' }}>🔍</span>
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    marginBottom: '12px'
                  }}>
                    No facilities found for "<strong>{facilitySearchQuery}</strong>" in Sukoharjo area
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#9ca3af',
                    lineHeight: '1.5'
                  }}>
                    Try searching for:
                    <br />
                    <strong>Indonesian terms:</strong> spbu, rumah sakit, apotek, pasar, warung
                    <br />
                    <strong>English terms:</strong> hotel, restaurant, bank, hospital
                    <br />
                    <strong>Nearby cities:</strong> solo, surakarta, klaten
                  </div>
                  <button
                    onClick={() => setShowSearchResults(false)}
                    style={{
                      marginTop: '12px',
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      color: '#6b7280'
                    }}
                  >
                    Close
                  </button>
                </div>
              )
            )}
          </div>
        </div>
        
        {/* Route Information Panel */}
        {routeInfo && (
          <div style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxWidth: '300px',
            maxHeight: '400px',
            overflow: 'auto',
            border: '1px solid #e5e7eb',
            zIndex: 1000
          }}>
            <h3 style={{ margin: '0 0 15px', color: '#374151', fontSize: '18px' }}>
              🧭 Route Details
            </h3>
            
            <div style={{ marginBottom: '15px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div style={{ textAlign: 'center', padding: '10px', background: '#dbeafe', borderRadius: '6px' }}>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1d4ed8' }}>
                    {routeInfo.distance.toFixed(1)} km
                  </div>
                  <div style={{ fontSize: '12px', color: '#1e40af' }}>Distance</div>
                </div>
                <div style={{ textAlign: 'center', padding: '10px', background: '#dcfce7', borderRadius: '6px' }}>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#059669' }}>
                    {Math.round(routeInfo.duration)} min
                  </div>
                  <div style={{ fontSize: '12px', color: '#047857' }}>Duration</div>
                </div>
              </div>
            </div>
            
            {routeInfo.instructions && routeInfo.instructions.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 10px', color: '#374151', fontSize: '14px' }}>Turn-by-turn Directions</h4>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {routeInfo.instructions.slice(0, 5).map((step, index) => (
                    <div 
                      key={index} 
                      style={{ 
                        padding: '8px', 
                        background: index % 2 === 0 ? '#f9fafb' : 'white',
                        borderRadius: '4px',
                        marginBottom: '4px',
                        fontSize: '12px',
                        color: '#374151'
                      }}
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                        {index + 1}. {step.maneuver?.instruction || 'Continue'}
                      </div>
                      {step.distance && (
                        <div style={{ color: '#6b7280' }}>
                          {step.distance > 1000 
                            ? `${(step.distance / 1000).toFixed(1)} km` 
                            : `${Math.round(step.distance)} m`
                          }
                        </div>
                      )}
                    </div>
                  ))}
                  {routeInfo.instructions.length > 5 && (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '8px', 
                      color: '#6b7280', 
                      fontSize: '12px',
                      fontStyle: 'italic'
                    }}>
                      + {routeInfo.instructions.length - 5} more steps...
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <button
              onClick={stopNavigation}
              style={{
                background: '#ef4444',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                marginTop: '15px',
                width: '100%'
              }}
            >
              Clear Route
            </button>
          </div>
        )}
      </div>

      {/* POI Details Modal */}
      {selectedPOI && (
        <div className="poi-modal-overlay" onClick={() => {
          setSelectedPOI(null);
          setSelectedImageIndex(null);
          setUserRating(0);
          setHoveredRating(0);
          setIsSubmittingRating(false);
        }}>
          <div className="poi-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={() => {
              setSelectedPOI(null);
              setSelectedImageIndex(null);
              setUserRating(0);
              setHoveredRating(0);
              setIsSubmittingRating(false);
            }}>
              ×
            </button>
            
            {/* POI Title */}
            <h2>{selectedPOI.name}</h2>
            
            {/* Category and District Info */}
            <div className="poi-category-district">
              {typeof selectedPOI.category === 'object' && (
                <span className="poi-category-badge">
                  📍 {selectedPOI.category.name}
                </span>
              )}
              {typeof selectedPOI.layer === 'object' && (
                <span className="poi-district-badge">
                  🏘️ {selectedPOI.layer.name}
                </span>
              )}
              {selectedPOI.status && (
                <span className="poi-status-badge" style={{
                  background: selectedPOI.status === 4 ? '#10b981' : selectedPOI.status === 3 ? '#f59e0b' : selectedPOI.status === 2 ? '#3b82f6' : '#6b7280',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  margin: '2px'
                }}>
                  🏢 {POI_STATUS_LABELS[selectedPOI.status as keyof typeof POI_STATUS_LABELS]}
                </span>
              )}
            </div>
            
            {/* Address */}
            {selectedPOI.address && (
              <p className="poi-address">{selectedPOI.address}</p>
            )}
            
            {/* Images */}
            {selectedPOI.images && selectedPOI.images.length > 0 && (
              <div className="poi-images">
                {selectedPOI.images.map((image, index) => (
                  <img 
                    key={index} 
                    src={image.url} 
                    alt={image.caption || selectedPOI.name}
                    onClick={() => setSelectedImageIndex(index)}
                    className="clickable-image"
                  />
                ))}
              </div>
            )}
            
            {/* Description */}
            <div className="poi-description-section">
              <p className="poi-description">{selectedPOI.description}</p>
            </div>
            
            {/* Video */}
            {selectedPOI.video && (
              <div className="poi-video-section">
                <h4>Video</h4>
                <div className="video-container">
                  {selectedPOI.video.includes('youtube.com') || selectedPOI.video.includes('youtu.be') ? (
                    <iframe
                      src={getYouTubeEmbedUrl(selectedPOI.video)}
                      title="POI Video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ width: '100%', height: '200px', borderRadius: '8px' }}
                    />
                  ) : selectedPOI.video.includes('vimeo.com') ? (
                    <iframe
                      src={getVimeoEmbedUrl(selectedPOI.video)}
                      title="POI Video"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      style={{ width: '100%', height: '200px', borderRadius: '8px' }}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                      <a 
                        href={selectedPOI.video} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#3b82f6', textDecoration: 'none' }}
                      >
                        🎥 Watch Video
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Rating Section */}
            <div className="poi-rating-section">
              <div className="current-rating">
                <span>★ {selectedPOI.rating.toFixed(1)}</span>
                <span>({selectedPOI.reviewCount} reviews)</span>
              </div>
              
              <div className="user-rating">
                <span className="rating-label">Rate this place:</span>
                <div className="stars-container">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      className={`star-button ${
                        star <= (hoveredRating || userRating) ? 'filled' : 'empty'
                      } ${isSubmittingRating ? 'submitting' : ''}`}
                      onMouseEnter={() => !isSubmittingRating && setHoveredRating(star)}
                      onMouseLeave={() => !isSubmittingRating && setHoveredRating(0)}
                      onClick={() => handleRatingClick(star)}
                      disabled={isSubmittingRating}
                      title={isSubmittingRating ? 'Submitting rating...' : `Rate ${star} star${star > 1 ? 's' : ''}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
                {isSubmittingRating && (
                  <span className="rating-feedback">Submitting rating...</span>
                )}
                {userRating > 0 && !isSubmittingRating && (
                  <span className="rating-feedback success">Thank you for your rating!</span>
                )}
              </div>
            </div>
            
            {/* Contact Information */}
            <div className="poi-contact-section">
              <div className="contact-row">
                {selectedPOI.contact?.phone && (
                  <span className="contact-item">
                    📞 {selectedPOI.contact.phone}
                  </span>
                )}
                {selectedPOI.contact?.email && (
                  <span className="contact-item">
                    ✉️ {selectedPOI.contact.email}
                  </span>
                )}
              </div>
            </div>
            
            {/* Links Section */}
            <div className="poi-links-section">
              <button
                onClick={() => navigateToPoint([selectedPOI.long, selectedPOI.lat], selectedPOI.name)}
                style={{
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  width: '100%',
                  marginBottom: '10px'
                }}
              >
                🧭 Navigate Here
              </button>
              
              {selectedPOI.contact?.website && (
                <a 
                  href={selectedPOI.contact.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="poi-link"
                >
                  🌐 Website
                </a>
              )}
              {selectedPOI.links && selectedPOI.links.length > 0 && (
                <div className="poi-additional-links">
                  {selectedPOI.links.map((link, index) => (
                    <a 
                      key={index}
                      href={link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="poi-link"
                      style={{ display: 'block', marginBottom: '4px' }}
                    >
                      🔗 {new URL(link).hostname}
                    </a>
                  ))}
                </div>
              )}
              <a 
                href={getGoogleMapsUrl(selectedPOI.lat, selectedPOI.long)}
                target="_blank" 
                rel="noopener noreferrer"
                className="poi-link"
              >
                📍 {selectedPOI.lat.toFixed(6)}, {selectedPOI.long.toFixed(6)}
              </a>
            </div>
            
            {/* Opening Hours */}
            {selectedPOI.openingHours && (
              <div className="poi-opening-hours">
                <h4>Opening Hours</h4>
                <div className="hours-list">
                  {formatOpeningHours(selectedPOI.openingHours)?.map((dayHours, index) => (
                    <div key={index} className="hours-row">
                      <span className="day">{dayHours.day}:</span>
                      <span className="hours">{dayHours.hours}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Image Enlargement Modal */}
      {selectedImageIndex !== null && selectedPOI && selectedPOI.images && (
        <div className="image-modal-overlay" onClick={() => setSelectedImageIndex(null)}>
          <div className="image-modal" onClick={(e) => e.stopPropagation()}>
            <button className="image-close-button" onClick={() => setSelectedImageIndex(null)}>
              ×
            </button>
            <img 
              src={selectedPOI.images[selectedImageIndex].url} 
              alt={selectedPOI.images[selectedImageIndex].caption || selectedPOI.name}
              className="enlarged-image"
            />
            {selectedPOI.images[selectedImageIndex].caption && (
              <p className="image-caption">{selectedPOI.images[selectedImageIndex].caption}</p>
            )}
            
            {/* Navigation arrows */}
            {selectedPOI.images.length > 1 && (
              <>
                <button 
                  className="nav-arrow prev-arrow"
                  onClick={() => setSelectedImageIndex(
                    selectedImageIndex === 0 
                      ? selectedPOI.images.length - 1 
                      : selectedImageIndex - 1
                  )}
                >
                  ‹
                </button>
                <button 
                  className="nav-arrow next-arrow"
                  onClick={() => setSelectedImageIndex(
                    selectedImageIndex === selectedPOI.images.length - 1 
                      ? 0 
                      : selectedImageIndex + 1
                  )}
                >
                  ›
                </button>
              </>
            )}
            
            {/* Image counter */}
            <div className="image-counter">
              {selectedImageIndex + 1} / {selectedPOI.images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
